/**
 * Sector operations — CSV Import / Export Service (R39 A13.a, SO-P69).
 * Callable wrappers and import job subscription.
 */

import {
  doc,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  ImportOpsCsvInput,
  ImportOpsCsvResult,
  ExportOpsCsvInput,
  ExportOpsCsvResult,
  OpsImportJobDoc,
  OpsImportFileRefusalReason,
  OpsImportErrorCode,
  OpsImportEntity,
  OpsSector,
} from '../types/ops'
import { CsvParseError } from '../types/ops'
import { getNlsString } from '../nls/nlsRuntime'
import { downloadCsvText } from '../utils/exportCsv'

export async function importOpsCsv(
  input: ImportOpsCsvInput,
): Promise<ImportOpsCsvResult> {
  return callable<ImportOpsCsvInput, ImportOpsCsvResult>('importOpsCsv', input)
}

export async function exportOpsCsv(
  input: ExportOpsCsvInput,
): Promise<ExportOpsCsvResult> {
  return callable<ExportOpsCsvInput, ExportOpsCsvResult>('exportOpsCsv', input)
}

export function buildOpsCsvFilename(
  entity: OpsImportEntity | string,
  sector?: OpsSector,
): string {
  const dateStr = new Date().toISOString().slice(0, 10)
  const prefix = sector ? `${sector.toLowerCase()}-` : ''
  return `${prefix}${entity.toLowerCase()}-${dateStr}.csv`
}

export function downloadOpsCsv(
  filenameOrEntity: string,
  csvText: string,
  sector?: OpsSector,
): void {
  const filename = filenameOrEntity.endsWith('.csv')
    ? filenameOrEntity
    : buildOpsCsvFilename(filenameOrEntity, sector)
  downloadCsvText(filename, csvText)
}

export interface ClassifiedOpsImportError {
  message: string
  reason?: OpsImportFileRefusalReason
  code?: string
}

export function classifyOpsImportError(err: unknown): ClassifiedOpsImportError {
  if (err instanceof CsvParseError) {
    const reason = err.reason
    return {
      message: getFileRefusalNlsMessage(reason),
      reason,
      code: 'invalid-argument',
    }
  }

  const errObj = typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : null
  const details = (errObj?.details ?? (errObj?.cause as Record<string, unknown>)?.details) as
    | { reason?: unknown; columns?: unknown; count?: unknown }
    | undefined
  const reason = typeof details?.reason === 'string' ? (details.reason as OpsImportFileRefusalReason) : undefined
  const code = typeof errObj?.code === 'string' ? errObj.code.replace(/^functions\//, '') : undefined

  if (reason) {
    return {
      message: getFileRefusalNlsMessage(reason, details),
      reason,
      code,
    }
  }

  if (code === 'permission-denied') {
    return {
      message: getNlsString('NLSOpsCsvPermissionDenied'),
      code,
    }
  }

  if (code === 'unavailable' || code === 'deadline-exceeded') {
    return {
      message: getNlsString('NLSOpsCsvNetworkError'),
      code,
    }
  }

  return {
    message: getNlsString('NLSOpsCsvGenericError'),
    code,
  }
}

function getFileRefusalNlsMessage(
  reason: OpsImportFileRefusalReason,
  details?: { columns?: unknown; count?: unknown },
): string {
  switch (reason) {
    case 'TOO_LARGE':
      return getNlsString('NLSOpsCsvFileErrorTooLarge')
    case 'TOO_MANY_ROWS':
      return getNlsString('NLSOpsCsvFileErrorTooManyRows')
    case 'TOO_MANY_COLUMNS':
      return getNlsString('NLSOpsCsvFileErrorTooManyColumns')
    case 'CELL_TOO_LONG':
      return getNlsString('NLSOpsCsvFileErrorCellTooLong')
    case 'UNTERMINATED_QUOTE':
      return getNlsString('NLSOpsCsvFileErrorUnterminatedQuote')
    case 'PARSE_ERROR':
      return getNlsString('NLSOpsCsvFileErrorParseError')
    case 'MISSING_COLUMNS': {
      const cols = Array.isArray(details?.columns) ? details.columns.join(', ') : ''
      return getNlsString('NLSOpsCsvFileErrorMissingColumns', { columns: cols })
    }
    case 'ROW_ERRORS':
      return getNlsString('NLSOpsCsvFileErrorRowErrors')
    case 'OPID_REUSED':
      return getNlsString('NLSOpsCsvFileErrorOpIdReused')
    case 'EXPORT_TOO_LARGE':
      return getNlsString('NLSOpsCsvFileErrorExportTooLarge')
    case 'NO_PLACE_LEVEL':
      return getNlsString('NLSOpsCsvFileErrorNoPlaceLevel')
    default:
      return getNlsString('NLSOpsCsvGenericError')
  }
}

export function formatOpsImportRowError(
  code: OpsImportErrorCode,
): string {
  switch (code) {
    case 'REQUIRED':
      return getNlsString('NLSOpsCsvRowErrorRequired')
    case 'TOO_LONG':
      return getNlsString('NLSOpsCsvRowErrorTooLong')
    case 'BAD_NUMBER':
      return getNlsString('NLSOpsCsvRowErrorBadNumber')
    case 'BAD_DATE':
      return getNlsString('NLSOpsCsvRowErrorBadDate')
    case 'BAD_VALUE':
      return getNlsString('NLSOpsCsvRowErrorBadValue')
    case 'DUPLICATE_IN_FILE':
      return getNlsString('NLSOpsCsvRowErrorDuplicateInFile')
    case 'UNKNOWN_PLACE':
      return getNlsString('NLSOpsCsvRowErrorUnknownPlace')
    case 'UNKNOWN_PARTY':
      return getNlsString('NLSOpsCsvRowErrorUnknownParty')
    case 'UNKNOWN_SUPPLIER':
      return getNlsString('NLSOpsCsvRowErrorUnknownSupplier')
    case 'AMBIGUOUS_SUPPLIER':
      return getNlsString('NLSOpsCsvRowErrorAmbiguousSupplier')
    case 'NOT_A_MEMBER':
      return getNlsString('NLSOpsCsvRowErrorNotAMember')
    case 'ROLE_NOT_ALLOWED':
      return getNlsString('NLSOpsCsvRowErrorRoleNotAllowed')
    case 'QUANTITY_IGNORED':
      return getNlsString('NLSOpsCsvRowErrorQuantityIgnored')
    case 'BAD_SECTOR':
      return getNlsString('NLSOpsCsvRowErrorBadSector')
    case 'CORE_REFUSED':
      return getNlsString('NLSOpsCsvRowErrorCoreRefused')
    default:
      return getNlsString('NLSOpsCsvRowErrorGeneric')
  }
}

export function subscribeImportJob(
  workspaceId: string,
  opId: string,
  onUpdate: (job: OpsImportJobDoc | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const docRef = doc(firestoreDb, 'ops_import_jobs', `${workspaceId}_${opId}`)
  return onSnapshot(
    docRef,
    (snap) => {
      if (!snap.exists()) {
        onUpdate(null)
        return
      }
      onUpdate(snap.data() as OpsImportJobDoc)
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}
